import { Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, InteractionStatus } from '@azure/msal-browser';
import { filter, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Component({
    selector: 'rax-shell-auth-page',
    templateUrl: './auth-page.component.html',
    imports: [MatButtonModule, MatIconModule],
})
export class ShellAuthPage implements OnInit, OnDestroy {
    private readonly msalBroadcastService = inject(MsalBroadcastService);
    private readonly authService = inject(MsalService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    private authResult: AuthenticationResult | null | undefined;
    private msalCallbackId: string | null = null;
    public readonly accounts = signal<AccountInfo[]>([]);
    public readonly showLoading = signal<boolean>(true);

    public ngOnDestroy(): void {
        if (this.msalCallbackId != undefined) {
            this.authService.instance.removeEventCallback(this.msalCallbackId);
        }
    }

    public ngOnInit(): void {
        this.msalCallbackId = this.authService.instance.addEventCallback((message: EventMessage) => {
            console.log('MSAL EventCallback', message);
        });

        this.authService
            .handleRedirectObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result: AuthenticationResult) => {
                    console.log('AuthPage handleRedirectObservable next');
                    console.log(result);

                    this.accounts.set(this.authService.instance.getAllAccounts());
                    console.log('HandleRedirectObservable All Accounts Log:', this.accounts());

                    this.authResult = result;
                },
                error: (err) => {
                    console.log('AuthPage handleRedirectObservable err');
                    console.log(err);
                },
                complete: () => {
                    console.log('AuthPage handleRedirectObservable complete');
                },
            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => {
                    return status === InteractionStatus.None;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                console.log('Auth idle');
                if (this.authResult === null) {
                    console.log('authresult null, redirect login');
                    this.router.navigateByUrl('login');
                } else {
                    if (this.authResult != undefined) {
                        const now = new Date();
                        const expiresOn = this.authResult.expiresOn;
                        if (!expiresOn || now >= expiresOn) {
                            console.log('authresult expired, redirect login');
                            this.router.navigateByUrl('login');
                            return;
                        }
                    }

                    this.accounts.set(this.authService.instance.getAllAccounts());
                    console.log('Progress All Accounts Log:', this.accounts());

                    if (this.accounts()?.length === 1) {
                        console.log('authresult ok, redirect loading');
                        this.router.navigateByUrl('loading');
                    } else {
                        console.log('authresult ok, other than 1 account');

                        const activeAccount = this.authService.instance.getActiveAccount();
                        if (activeAccount !== null) {
                            this.router.navigateByUrl('loading');
                        } else {
                            this.showLoading.set(false);
                        }
                    }
                }
            });
    }

    public onSetActiveAccountButtonClick(account: AccountInfo): void {
        this.showLoading.set(true);
        this.authService.instance.setActiveAccount(account);
        this.router.navigateByUrl('loading');
    }

    public onRemoveAccountButtonClick(account: AccountInfo): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => {
                    return status === InteractionStatus.None;
                }),
                take(1),
            )
            .subscribe(() => {
                this.authService.logoutRedirect({
                    account: account,
                });
            });
    }

    public onSignOutAllAccountsButtonClick(): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => {
                    return status === InteractionStatus.None;
                }),
                take(1),
            )
            .subscribe(() => {
                this.authService.logoutRedirect();
            });
    }

    public onSignInWithDifferentAccountButtonClick(): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => {
                    return status === InteractionStatus.None;
                }),
                take(1),
            )
            .subscribe(() => {
                this.authService.loginRedirect({
                    scopes: environment.msalProtectedResources.RaxBFFApi.scopes,
                    prompt: 'select_account',
                    extraQueryParameters: {
                        ui_locales: 'nl',
                    },
                });
            });
    }
}
